import { RefObject, useEffect } from 'react';

import { MotionValue, useMotionValue } from 'motion/react';

/** Delay after the user stops scrolling before the toolbar snaps to an end. */
const SNAP_DEBOUNCE_MS = 250;
/** Time given for the programmatic smooth-scroll to settle before re-arming. */
const SNAP_SETTLE_MS = 400;

interface UseCollapseScrollResult {
  /** Live `scrollTop` of the container. Drive Motion transforms off this. */
  scrollY: MotionValue<number>;
}

/**
 * Tracks a scroll container's `scrollTop` into a Motion `MotionValue` and adds
 * snap-to-end behavior: when the user stops scrolling mid-transition (between
 * `0` and `collapseDistancePx`), the container smooth-scrolls to whichever end
 * is nearer, so the toolbar never rests half-collapsed.
 *
 * The MotionValue is updated outside React state, so consumers can derive
 * transforms with `useTransform` without re-rendering on every scroll frame.
 */
export const useCollapseScroll = (
  scrollContainerRef: RefObject<HTMLElement | null>,
  collapseDistancePx: number
): UseCollapseScrollResult => {
  const scrollY = useMotionValue(0);

  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return undefined;

    let snapTimer: ReturnType<typeof setTimeout> | undefined;
    let isSnapping = false;

    const snapToNearestEnd = () => {
      if (isSnapping) return;
      const top = node.scrollTop;
      const isMidTransition = top > 0 && top < collapseDistancePx;
      if (!isMidTransition) return;

      const target = top < collapseDistancePx / 2 ? 0 : collapseDistancePx;
      isSnapping = true;
      node.scrollTo({ top: target, behavior: 'smooth' });
      window.setTimeout(() => {
        isSnapping = false;
      }, SNAP_SETTLE_MS);
    };

    const handleScroll = () => {
      scrollY.set(node.scrollTop);
      if (snapTimer) clearTimeout(snapTimer);
      snapTimer = setTimeout(snapToNearestEnd, SNAP_DEBOUNCE_MS);
    };

    handleScroll();
    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      node.removeEventListener('scroll', handleScroll);
      if (snapTimer) clearTimeout(snapTimer);
    };
    // `scrollY` is a stable MotionValue; only re-bind if the distance changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseDistancePx]);

  return { scrollY };
};
